// Phase 5Z.15 — Audit page with role-based tabs and date range filters
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SectionHeader } from "@/components/Shared";
import DataTable from "@/components/DataTable";
import { Users, Truck, FileText, Calculator, Briefcase, Shield, Layers } from "lucide-react";
import ModuleHint from "@/components/ModuleHint";
import FirmFilter from "@/components/FirmFilter";

const TABS = [
  { key: "alle", label: "Alle", icon: Layers, color: "text-zinc-300" },
  { key: "fahrer", label: "Fahrer", icon: Truck, color: "text-amber-400" },
  { key: "disposition", label: "Disposition", icon: Users, color: "text-sky-400" },
  { key: "fuhrpark", label: "Fuhrpark", icon: Truck, color: "text-blue-400" },
  { key: "backoffice", label: "Backoffice", icon: FileText, color: "text-emerald-400" },
  { key: "buchhaltung", label: "Buchhaltung", icon: Calculator, color: "text-green-400" },
  { key: "leitung", label: "Leitung & Admin", icon: Briefcase, color: "text-violet-400" },
  { key: "system", label: "System & Viewer", icon: Shield, color: "text-zinc-500" },
];

export default function Audit() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  const [category, setCategory] = useState("alle");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = { q, module, action, category, date_from: dateFrom, date_to: dateTo };
      const [{ data }, { data: sum }] = await Promise.all([
        api.get("/audit", { params }),
        api.get("/audit/_summary", { params: { date_from: dateFrom, date_to: dateTo } }),
      ]);
      setItems(data);
      setCounts(sum?.counts || {});
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [q, module, action, category, dateFrom, dateTo]);

  return (
    <div className="p-6 md:p-8" data-testid="audit-page">
      <SectionHeader title="Audit" subtitle="Lückenlose Änderungshistorie — Wer · Wann · Was · Wo" />
      <ModuleHint moduleKey="audit" />

      {/* Tabs by role-category */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-zinc-800" data-testid="audit-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const n = counts[t.key] || 0;
          const active = category === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setCategory(t.key)}
              data-testid={`audit-tab-${t.key}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                active
                  ? "border-amber-500 text-zinc-100"
                  : "border-transparent text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
              }`}
            >
              <Icon className={`w-3.5 h-3.5 ${active ? t.color : ""}`} />
              <span>{t.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-amber-500/20 text-amber-300" : "bg-zinc-800 text-zinc-500"}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      <DataTable
        rows={items}
        loading={loading}
        searchTerm={q}
        onSearchChange={setQ}
        searchPlaceholder="Benutzer / E-Mail / Aktion / Modul / Datensatz-ID..."
        testid="audit-table"
        emptyTitle="Keine Audit-Einträge"
        emptyHint={q || module || action || dateFrom || dateTo ? "Versuchen Sie andere Filter." : "Sobald Aktionen ausgeführt werden, erscheinen sie hier."}
        defaultPageSize={50}
        filters={
          <>
            <FirmFilter compact />
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" />
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100" />
            <select value={module} onChange={(e) => setModule(e.target.value)} data-testid="audit-module-filter"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
              <option value="">Alle Module</option>
              {["employees", "vehicles", "time_entries", "driving_logs", "shifts", "documents", "exports", "settings", "alerts", "tasks", "damages", "checklists", "templates", "cards", "card_history", "handovers", "profile_change_requests"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action-filter"
              className="px-3 py-2 bg-zinc-950 border border-zinc-800 rounded text-sm text-zinc-100">
              <option value="">Alle Aktionen</option>
              {["create", "update", "delete", "export", "send", "run", "bulk_update", "approve", "reject", "assign", "enable_portal", "disable_portal"].map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            {(dateFrom || dateTo || module || action || q) && (
              <button onClick={() => { setQ(""); setModule(""); setAction(""); setDateFrom(""); setDateTo(""); }}
                      data-testid="audit-clear-filters"
                      className="px-3 py-2 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded">
                Zurücksetzen
              </button>
            )}
          </>
        }
        columns={[
          { key: "timestamp", label: "Wann", sortable: true,
            render: (a) => <span className="font-mono text-xs text-zinc-400">{new Date(a.timestamp).toLocaleString("de-AT")}</span> },
          { key: "user_name", label: "Wer", sortable: true,
            render: (a) => (
              <div>
                <div className="text-zinc-100 font-medium text-sm">{a.user_name || "—"}</div>
                <div className="text-[10px] text-zinc-500">
                  {a.user_role && <span className="px-1 py-0.5 bg-zinc-800 rounded mr-1.5 capitalize">{a.user_role}</span>}
                  {a.user_email}
                </div>
              </div>
            ) },
          { key: "action", label: "Was", sortable: true,
            render: (a) => <span className={`font-mono text-xs ${a.action === "create" ? "text-emerald-400" : a.action === "delete" || a.action === "reject" ? "text-red-400" : a.action === "approve" ? "text-emerald-300" : a.action === "export" ? "text-blue-400" : "text-amber-400"}`}>{a.action}</span> },
          { key: "module", label: "Wo (Modul)", sortable: true,
            render: (a) => <span className="text-zinc-300 font-mono text-xs">{a.module}</span> },
          { key: "record_id", label: "Datensatz",
            render: (a) => <span className="text-zinc-500 font-mono text-xs truncate max-w-[180px] inline-block" title={a.record_id}>{(a.record_id || "—").slice(0, 13)}{(a.record_id || "").length > 13 ? "…" : ""}</span> },
        ]}
      />
    </div>
  );
}
